import { observable } from "mobx";

import { downloadFile } from "../utils/downloadFile";
import { IAppStore } from "./IAppStore";

const mergeAudioStreams = (
    desktopStream: MediaStream,
    voiceStream: MediaStream | undefined,
) => {
    const context = new AudioContext();
    const destination = context.createMediaStreamDestination();
    let hasDesktop = false;
    let hasVoice = false;
    if (desktopStream && desktopStream.getAudioTracks().length > 0) {
        // If you don't want to share Audio from the desktop it should still work with just the voice.
        const source1 = context.createMediaStreamSource(desktopStream);
        const desktopGain = context.createGain();
        desktopGain.gain.value = 0.7;
        source1.connect(desktopGain).connect(destination);
        hasDesktop = true;
    }

    if (voiceStream && voiceStream.getAudioTracks().length > 0) {
        const source2 = context.createMediaStreamSource(voiceStream);
        const voiceGain = context.createGain();
        voiceGain.gain.value = 0.7;
        source2.connect(voiceGain).connect(destination);
        hasVoice = true;
    }

    return hasDesktop || hasVoice ? destination.stream.getAudioTracks() : [];
};

export class ScreenRecordingStore {
    @observable
    isRecording = false;
    stream?: any;
    recorder?: any;

    constructor(private appStore: IAppStore) {}

    public async startRecordingVideo(
        fileName: string,
        desktopAudio: boolean = true,
        micAudio: boolean = true,
    ) {
        try {
            let voiceStream;

            const desktopStream = await navigator.mediaDevices.getDisplayMedia({
                video: true,
                audio: desktopAudio,
            });

            if (micAudio === true) {
                voiceStream = await navigator.mediaDevices.getUserMedia({
                    video: false,
                    audio: micAudio,
                });
            }

            const tracks = [
                ...desktopStream.getVideoTracks(),
                ...mergeAudioStreams(desktopStream, voiceStream),
            ];

            const stream = new MediaStream(tracks);
            this.isRecording = true;

            const recorder = new MediaRecorder(stream);

            this.stream = stream;
            this.recorder = recorder;

            const chunks: Blob[] = [];
            recorder.ondataavailable = e => chunks.push(e.data);

            recorder.onstop = _ => {
                this.isRecording = false;

                const blob = new Blob(chunks, { type: chunks[0].type });
                const ext = blob.type.includes("webm") ? "webm" : "mp4";
                downloadFile(`${fileName}.${ext}`, blob.type, blob);
            };

            recorder.start();
        } catch (_) {
            this.appStore.setSnackbar("Failed to start recording video.");
            return;
        }
    }

    public stopRecordingVideo() {
        this.isRecording = false;
        this.recorder.stop();
        this.stream.getVideoTracks()[0].stop();
    }
}
